export const CURRENT_URL = `${window.location.protocol}//${window.location.host}`;
import { useState, useEffect, useRef } from "react";
import { GridLoader } from "react-spinners";
import { QRCodeCanvas } from "qrcode.react";
import * as Dialog from "@radix-ui/react-dialog";
import * as Form from "@radix-ui/react-form";
import * as RadioGroup from "@radix-ui/react-radio-group";
import * as Separator from "@radix-ui/react-separator";
import * as Toast from "@radix-ui/react-toast";
import { CheckCircle, CircleX } from "lucide-react";
import { EyeOff, Eye, RefreshCcw, MapPinCheckInside } from "lucide-react";

const query = new URLSearchParams(window.location.search);

const JointEvent = () => {
  const [isStatusChecked, setIsStatusChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [qrValue, setQrValue] = useState("");
  const [successfullyJoin, setSuccessfullyJoin] = useState(false);
  const [alreadyJoined, setAlreadyJoined] = useState(false);
  const [userId, setUserId] = useState(0);
  const [eventId, setEventId] = useState(0);
  const [step, setStep] = useState(0);
  const [bringingSomeone, setBringingSomeone] = useState("no");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    age: "",
    gender: "",
    email: "",
    phone: "",
    password: "",
    isTermsAndConditionsAccepted: false,
    isSubscribedToNewsletter: true,
  });
  const [eventData, setEventData] = useState({
    adultCount: 0,
    childCount: 0,
  });
  const [attendanceCode, setAttendanceCode] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [isError, setIsError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  useEffect(() => {
    if (userId === 0) {
      return;
    }

    const param = query.get("open_join");

    if (!param) {
      return;
    }

    if (!isStatusChecked) {
      return;
    }

    const isOpenJoin = param.toLowerCase() === "true";

    if (isOpenJoin && !alreadyJoined) {
      setIsDialogOpen(true);
    } else {
      setIsDialogOpen(false);
    }
  }, [userId, alreadyJoined, isStatusChecked]);

  const handleToggleTermsCheckbox = () => {
    setFormData((prev) => ({
      ...prev,
      isTermsAndConditionsAccepted: !prev.isTermsAndConditionsAccepted,
    }));
  };

  const handleToggleNewsletterCheckbox = () => {
    setFormData((prev) => ({
      ...prev,
      isSubscribedToNewsletter: !prev.isSubscribedToNewsletter,
    }));
  };

  const handlePreOpen = async () => {
    const response = await fetch(tmtUser.endpoint.profile_id, {
      headers: {
        "X-WP-Nonce": tmtUser.nonce,
      },
    });

    const user_id = await response.json();
    setUserId(user_id);
  };

  const goNext = () => setStep((prev) => Math.min(prev + 1, 2));
  const goBack = () => setStep((prev) => Math.max(prev - 1, 0));

  const generatePassword = () => {
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*";

    let pw = "";
    for (let i = 0; i < 10; i++) {
      pw += charset.charAt(Math.floor(Math.random() * charset.length));
    }

    setFormData({ ...formData, password: pw });
  };

  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCloseDialog = () => setIsDialogOpen(false);

  const handleFormDataChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEventDataChange = (e) => {
    setEventData({ ...eventData, [e.target.name]: e.target.value });
  };

  const handleAccountRegister = async (e) => {
    e.preventDefault();

    setProcessing(true);

    try {
      const response = await fetch(tmtUser.endpoint.register, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": tmtUser.nonce,
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        setIsError(true);
        setOpenToast(true);
        console.log(result);
        setToastMessage(result.message);
        throw new Error(result?.message || "Account creation failed.");
      }

      if (!Number.isInteger(result)) {
        throw new Error(
          "Unexpected response format. Expected user ID as an integer."
        );
      }

      setIsError(false);

      window.location.href = "?open_join=true";

      // setUserId(result);
    } catch (error) {
      console.log(error);
    } finally {
      setProcessing(false);
    }
  };

  const handleStep0Click = () => {
    if (bringingSomeone === "yes") {
      goNext();
    } else {
      handleEventRegister();
    }
  };

  const handleEventRegister = async (e) => {
    setProcessing(true);

    try {
      const response = await fetch(tmtUser.endpoint.join, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": tmtUser.nonce,
        },
        body: JSON.stringify({
          event_id: eventId,
          user_id: userId,
          extras: eventData,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.message || "Event registration failed.");
      }
      console.log(result);
      setSuccessfullyJoin(true);
      setAttendanceCode(result.attendance_code);
      setQrValue(result.url);
    } catch (error) {
      console.log(error);
    } finally {
      setProcessing(false);
    }
  };

  const checkAlreadyJoined = async () => {
    console.log("checking already joined...");

    try {
      const response = await fetch(tmtUser.endpoint.verify, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": tmtUser.nonce,
        },
        body: JSON.stringify({
          event: parseInt(eventId),
          user: parseInt(userId),
        }),
      });

      if (!response.ok) {
        throw new Error(response?.message || "Event check failed.");
      }

      const status = await response.json();

      setAttendanceCode(status.attendance_code);

      if (status.joined) {
        setAlreadyJoined(true);
      }

      setQrValue(status.url);
    } catch (error) {
      console.log(error);
    } finally {
      setIsStatusChecked(true);
    }
  };

  useEffect(() => {
    if (tmtUser.roles.includes("administrator")) {
      setIsAdmin(true);
    }

    handlePreOpen();
    generatePassword();
    setEventId(tmtUser.event_id);
  }, []);

  useEffect(() => {
    if (userId > 0) {
      try {
        checkAlreadyJoined();
      } catch (e) {}
    }
  }, [userId]);

  return (
    <>
      <Dialog.Root open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <Dialog.Trigger
          className={`wpem-theme-button ${alreadyJoined ? "disabled" : ""}`}
          disabled={alreadyJoined}
          onClick={handleOpenDialog}
        >
          <div className="join-button-contents">
            <MapPinCheckInside size={24} />
            {alreadyJoined ? "Already Joined" : "Event Registration | Ghi Danh"}
          </div>
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="DialogOverlay" />
          <Dialog.Content className="DialogContent">
            <Dialog.Title className="DialogTitle">
              {!successfullyJoin ? "Join Event" : "✅ Successfully Joined!"}
            </Dialog.Title>
            {userId === 0 && (
              <Form.Root onSubmit={handleAccountRegister}>
                <div className="FormCol">
                  <Form.Field name="firstName" className="FormField">
                    <Form.Label>First Name*</Form.Label>
                    <Form.Control asChild className="FormControl">
                      <input
                        type="text"
                        required
                        value={formData.firstName}
                        name="firstName"
                        onChange={handleFormDataChange}
                      />
                    </Form.Control>
                    <Form.Message match="valueMissing" className="FormError">
                      First name is required
                    </Form.Message>
                  </Form.Field>
                  <Form.Field name="lastName" className="FormField">
                    <Form.Label>Last Name*</Form.Label>
                    <Form.Control asChild className="FormControl">
                      <input
                        type="text"
                        required
                        value={formData.lastName}
                        name="lastName"
                        onChange={handleFormDataChange}
                      />
                    </Form.Control>
                    <Form.Message match="valueMissing" className="FormError">
                      Last name is required
                    </Form.Message>
                  </Form.Field>
                </div>
                <Form.Field name="emailAddress" className="FormField">
                  <Form.Label>Email Address* </Form.Label>
                  <Form.Control asChild className="FormControl">
                    <input
                      type="email"
                      required
                      value={formData.email}
                      name="email"
                      onChange={handleFormDataChange}
                    />
                  </Form.Control>
                  <Form.Message match="valueMissing" className="FormError">
                    Email address is required
                  </Form.Message>
                </Form.Field>
                <Form.Field name="gender" className="FormField">
                  <Form.Label htmlFor="genter">Gender*</Form.Label>
                  <select
                    id="gender"
                    required
                    value={formData.gender}
                    name="gender"
                    onChange={handleFormDataChange}
                  >
                    <option value="prefer_not_to_say">Prefer not to say</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                  <Form.Message match="valueMissing" className="FormError">
                    Gender is required
                  </Form.Message>
                </Form.Field>
                <Form.Field name="age" className="FormField">
                  <Form.Label>Age*</Form.Label>
                  <Form.Control asChild className="FormControl">
                    <input
                      type="number"
                      required
                      value={formData.age}
                      name="age"
                      onChange={handleFormDataChange}
                    />
                  </Form.Control>
                  <Form.Message match="valueMissing" className="FormError">
                    Age is required
                  </Form.Message>
                </Form.Field>
                <Form.Field name="phone" className="FormField">
                  <Form.Label>Contact Number</Form.Label>
                  <Form.Control asChild className="FormControl">
                    <input
                      type="tel"
                      value={formData.phone}
                      name="phone"
                      onChange={handleFormDataChange}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field name="company" className="FormField">
                  <Form.Label>Company</Form.Label>
                  <Form.Control asChild className="FormControl">
                    <input
                      type="text"
                      value={formData.company}
                      name="company"
                      onChange={handleFormDataChange}
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field name="password" className="FormField">
                  <Form.Label>Password*</Form.Label>
                  <div className="FormPasswordWrapper">
                    <Form.Control asChild className="FormControl">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleFormDataChange}
                      />
                    </Form.Control>
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label="Toggle password visibility"
                        style={{
                          cursor: "pointer",
                          transition: "color 0.2s ease",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = "#3b82f6")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = "black")
                        }
                      >
                        {showPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={generatePassword}
                        style={{
                          cursor: "pointer",
                          transition: "color 0.2s ease",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = "#3b82f6")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = "black")
                        }
                      >
                        <RefreshCcw size={20} />
                      </button>
                    </div>
                  </div>
                </Form.Field>
                <div className="checkbox-parent-container">
                  {/* <div className="checkbox-container">
                    <input
                      type="checkbox"
                      id="c1"
                      name="option1"
                      checked={formData.isSubscribedToNewsletter}
                      onChange={handleToggleNewsletterCheckbox}
                    />
                    <label className="Label" htmlFor="c1">
                      Subscribe to Newsletter
                    </label>
                  </div> */}
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      id="c2"
                      name="option2"
                      checked={formData.isTermsAndConditionsAccepted}
                      onChange={handleToggleTermsCheckbox}
                    />
                    <label className="Label" htmlFor="c2">
                      {/* Accept <a href="">Terms and Conditions.</a> */}
                      By signing up for this event, you agree to be added to our
                      mailing list for marketing purposes, including future
                      events and updates. You may unsubscribe at any time.
                    </label>
                  </div>
                </div>
                <Form.Submit asChild>
                  <button
                    className={`ButtonW100 wpem-theme-button ${processing || !formData.isTermsAndConditionsAccepted ? "disabled" : ""}`}
                    type="submit"
                    disabled={
                      processing || !formData.isTermsAndConditionsAccepted
                    }
                  >
                    {processing ? "Creating Account..." : "Create Account"}
                  </button>
                </Form.Submit>
              </Form.Root>
            )}
            {userId > 0 && !successfullyJoin && (
              <div className="slider-wrapper">
                <div
                  className="slider"
                  style={{ transform: `translateX(-${step * 100}%)` }}
                >
                  <div className="step">
                    <h3>Would you be bringing someone in the event?</h3>
                    <RadioGroup.Root
                      onValueChange={setBringingSomeone}
                      defaultValue="no"
                      className="RadioGroupRoot"
                    >
                      <div>
                        <RadioGroup.Item value="yes" id="accompany-yes">
                          <RadioGroup.Indicator className="RadioGroupIndicator" />
                        </RadioGroup.Item>
                        <label className="RadioLabel" htmlFor="accompany-yes">
                          Yes
                        </label>
                      </div>
                      <div>
                        <RadioGroup.Item value="no" id="accompany-no">
                          <RadioGroup.Indicator className="RadioGroupIndicator" />
                        </RadioGroup.Item>
                        <label className="RadioLabel" htmlFor="accompany-no">
                          No
                        </label>
                      </div>
                    </RadioGroup.Root>
                    <button
                      onClick={(e) =>
                        bringingSomeone
                          ? handleStep0Click(e)
                          : handleEventRegister(e)
                      }
                      disabled={processing}
                      className={`wpem-theme-button EventFormNextStep ${processing ? "disabled" : ""}`}
                      type="submit"
                    >
                      {bringingSomeone === "no"
                        ? processing
                          ? "Registering..."
                          : "Submit"
                        : "Next"}
                    </button>
                  </div>
                  <div className="step">
                    <Form.Root>
                      <Form.Field name="adultCount" className="FormField">
                        <Form.Label>Number of Adults*</Form.Label>
                        <Form.Control asChild className="FormControl">
                          <input
                            type="number"
                            required
                            min={1}
                            value={eventData.adultCount}
                            name="adultCount"
                            onChange={handleEventDataChange}
                          />
                        </Form.Control>
                        <Form.Message
                          match="valueMissing"
                          className="FormError"
                        >
                          Number of adults is required
                        </Form.Message>
                      </Form.Field>
                      <Form.Field name="childCount" className="FormField">
                        <Form.Label>Number of Children*</Form.Label>
                        <Form.Control asChild className="FormControl">
                          <input
                            type="number"
                            required
                            min={1}
                            value={eventData.childCount}
                            name="childCount"
                            onChange={handleEventDataChange}
                          />
                        </Form.Control>
                        <Form.Message
                          match="valueMissing"
                          className="FormError"
                        >
                          Number of children is required
                        </Form.Message>
                      </Form.Field>
                      <div className="EventFormActions">
                        <button onClick={goBack} type="submit">
                          Back
                        </button>
                        <button
                          onClick={(e) => handleEventRegister(e)}
                          className={`wpem-theme-button ${processing ? "disabled" : ""}`}
                          type="submit"
                          disabled={processing}
                        >
                          {processing ? "Processing..." : "Submit"}
                        </button>
                      </div>
                    </Form.Root>
                  </div>
                </div>
              </div>
            )}
            {successfullyJoin && (
              <div className="SuccessWrapper">
                <div className="QRCodeContainer">
                  <QRCodeCanvas
                    value={qrValue}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <p
                  style={{ fontWeight: "bolder" }}
                >{`Attendance code: ${attendanceCode}`}</p>
                <p>
                  Please take a screenshot on this QR code and present this QR
                  code on event day.
                </p>
                <br />
                <p>
                  You will also receive an email confirming in joining the event
                  with the QR code.
                </p>
                <button
                  className="wpem-theme-button"
                  onClick={handleCloseDialog}
                >
                  Close
                </button>
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      {alreadyJoined && (
        <div className="LabeledQRCodeContainer">
          <p>Entrance QR Code</p>
          <QRCodeCanvas
            value={qrValue}
            size={200}
            level="H"
            includeMargin={true}
          />
          <p>{`Attendance code: ${attendanceCode}`}</p>
        </div>
      )}
      <Toast.Provider swipeDirection="right">
        <Toast.Root
          className={`toast-root ${!isError ? "toast-success" : "toast-error"}`}
          open={openToast}
          onOpenChange={setOpenToast}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {!isError ? <CheckCircle size={20} /> : <CircleX size={20} />}
            <Toast.Title style={{ fontWeight: 600 }}>
              {!isError ? "Registration Successful!" : "Registration Failed"}
            </Toast.Title>
          </div>
          <Toast.Description style={{ fontSize: "0.9rem" }}>
            {!isError
              ? "Participant has been registered successfully."
              : toastMessage}
          </Toast.Description>
          <Toast.Close className="toast-close">✖</Toast.Close>
        </Toast.Root>

        <Toast.Viewport className="toast-viewport" />
      </Toast.Provider>
    </>
  );
};

export default JointEvent;
